import { masterAZ } from "./master.az";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { azUI } from "./az.ui";
import { azServices } from "./az.services";
import { azCases } from "./az.cases";
import { azInsights } from "./az.insights";
import { azSectors } from "./az.sectors";
import { langFromPath, localizePath } from "./routes";

export const AZ = { ...azUI, ...azServices, ...azCases, ...azInsights, ...azSectors, ...masterAZ };
const KEY = "yuxel-lang";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let overridesCache = null;
export const loadOverrides = (force = false) => {
  if (overridesCache && !force) return Promise.resolve(overridesCache);
  return fetch(`${API}/public/content`)
    .then((r) => (r.ok ? r.json() : {}))
    .catch(() => ({}))
    .then((m) => {
      overridesCache = m;
      return m;
    });
};
export const LANGS = ["en", "az"];

const stored = () => {
  try {
    const saved = localStorage.getItem(KEY);
    if (LANGS.includes(saved)) return saved;
  } catch {}
  return "az";
};
const hasSavedLanguage = () => {
  try {
    return LANGS.includes(localStorage.getItem(KEY));
  } catch {
    return false;
  }
};

const LangContext = createContext({ lang: "en", setLang: () => {}, t: (s) => s, href: (p) => p, ov: (k, f) => f });

export const LangProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const urlLang = langFromPath(location.pathname);
  const defaultAzWorkPath = !hasSavedLanguage() && /^\/work(?:\/|$)/.test(location.pathname);
  const [pref, setPref] = useState(stored);
  const [overrides, setOverrides] = useState(overridesCache || {});
  useEffect(() => {
    loadOverrides().then(setOverrides);
  }, []);
  const lang = defaultAzWorkPath ? "az" : urlLang || pref;

  useEffect(() => {
    if (!defaultAzWorkPath) return;
    const path = location.pathname + location.search + location.hash;
    const target = localizePath(path, "az");
    if (target !== path) navigate(target, { replace: true });
  }, [defaultAzWorkPath, location, navigate]);

  useEffect(() => {
    document.documentElement.lang = lang;
    try {
      localStorage.setItem(KEY, lang);
    } catch {}
    if (urlLang) setPref(urlLang);
  }, [lang, urlLang]);

  const setLang = useCallback(
    (l) => {
      if (!LANGS.includes(l) || l === lang) return;
      setPref(l);
      const target = localizePath(location.pathname + location.search + location.hash, l);
      if (target !== location.pathname + location.search + location.hash) navigate(target, { replace: true });
    },
    [lang, location, navigate]
  );

  const t = useCallback(
    (s) => {
      const o = overrides[s];
      if (o && o[lang]) return o[lang];
      return lang === "az" && s in AZ ? AZ[s] : s;
    },
    [lang, overrides]
  );
  const ov = useCallback(
    (key, fallback) => {
      const o = overrides[key];
      return (o && (o[lang] || o.en)) || fallback;
    },
    [lang, overrides]
  );
  const href = useCallback((p) => localizePath(p, lang), [lang]);
  const value = useMemo(() => ({ lang, setLang, t, href, ov }), [lang, setLang, t, href, ov]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);
export const missingAz = (strings) => strings.filter((s) => !(s in AZ));
